<template>
	<div
		:id="uniqueId"
		class="autocomplete-container"
		:class="{
			'is-open': isOpen,
		}"
	>
		<div
			class="input-container"
			:class="{
				'with-buttons': !!$slots.buttons,
			}"
		>
			<div
				class="input-wrapper"
				:class="{
					focused,
					filled: search.length,
					disabled,
				}"
			>
				<i
					v-if="!readonly"
					class="toggle-icon fas fa-chevron-down"
				></i>
				<input
					ref="input"
					v-model="search"
					type="text"
					class="form-control"
					:class="[...inputClasses]"
					:placeholder="selectedLabel || placeholder"
					:disabled="disabled"
					:readonly="readonly"
					@input="handleInput"
					@focus="handleFocus"
					@blur.prevent="handleBlur"
					@keydown.down="OnArrowDown"
					@keydown.up="OnArrowUp"
					@keydown.enter="OnEnter"
				/><i
					v-if="search.length && !readonly && !disabled"
					class="clear-icon fas fa-times text-2xl leading-none"
					@click.stop.prevent="ClearSearch()"
				></i>
				<div
					v-if="readonly"
					class="readonly-overlay"
				></div>
			</div>
			<div class="buttons-container">
				<slot name="buttons"></slot>
			</div>
		</div>
		<ul
			v-if="isOpen"
			class="options-list"
		>
			<template v-if="filteredOptions.length">
				<li
					v-for="(option, index) in filteredOptions"
					:key="option.value"
					class="option-item"
					:class="{
						selected: value && value === option.value,
						highlighted: index === highlightedIndex,
					}"
					@mousedown.prevent
					@click="Select(option)"
				>
					<slot
						name="option"
						:option="option"
					>
						{{ option.label }}
					</slot>
				</li>
			</template>
			<li
				v-else-if="minLength === 0"
				class="option-item no-options"
			>
				<slot name="no-options"> NENHUMA OPÇÃO ENCONTRADA. </slot>
			</li>
		</ul>
	</div>
</template>

<script>
	import { useGetUniqueId } from '@/composables'

	export default {
		name: 'Autocomplete',
		props: {
			value: {
				type: [Number, String],
				default: null,
			},
			options: {
				type: Array,
				required: true,
			},
			interceptSelect: {
				type: Boolean,
				default: false,
			},
			placeholder: {
				type: String,
				default: 'SELECIONE UMA OPÇÃO',
			},
			disabled: {
				type: Boolean,
				default: false,
			},
			readonly: {
				type: Boolean,
				default: false,
			},
			minLength: {
				type: [Number],
				default: 0,
			},
			inputClasses: {
				type: Array,
				default: () => [],
			},
		},
		data() {
			return {
				uniqueId: '',
				search: '',
				focused: false,
				isOpen: false,
				highlightedIndex: -1,
				optionWasSelected: false,
				isUserTyping: false,
			}
		},
		computed: {
			filteredOptions() {
				if (this.minLength > 0 && this.search.length < this.minLength) return []
				let filtereds = this.options

				if (!this.search.length) return filtereds
				if (this.optionWasSelected) return filtereds

				filtereds = filtereds.filter(option =>
					window.helpers
						.RemoveAcento(option.label)
						.toLowerCase()
						.includes(window.helpers.RemoveAcento(this.search).toLowerCase())
				)

				return filtereds
			},
			selectedLabel() {
				const selectedOption = this.options.find(option => this.value && option.value === this.value)
				return selectedOption ? selectedOption.label : null
			},
		},
		watch: {
			isOpen: {
				handler(newValue) {
					if (newValue) {
						window.addEventListener('click', this.handleClickOutside)
					} else {
						window.removeEventListener('click', this.handleClickOutside)
					}
				},
			},
			value: {
				handler(newValue) {
					if (this.isUserTyping && (newValue === null || newValue === undefined)) {
						return
					}
					this.syncSearchFromValue(newValue)
				},
				immediate: true,
			},
			options: {
				handler() {
					if (!this.focused) {
						this.syncSearchFromValue(this.value)
					}
				},
				deep: true,
			},
		},
		methods: {
			syncSearchFromValue(val) {
				if (val === null || val === undefined || val === '') {
					this.search = ''
					return
				}

				const selectedOption = this.options.find(
					option => option.value === val || (typeof val === 'object' && option.value === val?.value)
				)

				if (selectedOption && selectedOption.label) {
					this.search = selectedOption.label
				} else {
					this.search = String(val)
				}
			},
			handleInput() {
				this.isUserTyping = true
				this.optionWasSelected = false
				this.$emit('input', null)

				this.$nextTick(() => {
					this.isUserTyping = false
				})
			},
			handleFocus() {
				this.focused = true
				this.isOpen = true
				this.syncHighlightedIndex()
				this.$emit('focus')
			},
			handleBlur() {
				this.focused = false
			},
			handleClickOutside(event) {
				if (!this.$el.contains(event.target)) {
					this.closeAndBlur()
				}
			},
			closeAndBlur() {
				const hasSelection =
					this.$refs.input && this.$refs.input.selectionStart !== this.$refs.input.selectionEnd
				if (this.focused && hasSelection) {
					return
				}

				this.isOpen = false
				this.highlightedIndex = -1
				this.focused = false

				if (!this.value) {
					this.search = ''
				} else {
					this.syncSearchFromValue(this.value)
				}

				if (this.$refs.input) {
					this.$refs.input.blur()
					this.$emit('blur')
				}
			},
			syncHighlightedIndex() {
				if (!this.filteredOptions.length) {
					this.highlightedIndex = -1
					return
				}

				const selectedIndex = this.filteredOptions.findIndex(
					option => this.value && option.value === this.value
				)

				this.highlightedIndex = selectedIndex >= 0 ? selectedIndex : -1
			},
			moveHighlightedIndex(step) {
				if (!this.filteredOptions.length) {
					this.highlightedIndex = -1
					return
				}

				if (!this.isOpen) {
					this.isOpen = true
				}

				const currentIndex =
					this.highlightedIndex >= 0 && this.highlightedIndex < this.filteredOptions.length
						? this.highlightedIndex
						: step > 0
						? -1
						: 0
				const nextIndex = (currentIndex + step + this.filteredOptions.length) % this.filteredOptions.length

				this.highlightedIndex = nextIndex
			},
			Select(option) {
				this.isUserTyping = false
				this.optionWasSelected = true
				if (this.interceptSelect) {
					this.$emit('select', option.value)
				} else {
					this.$emit('input', option.value)
				}

				this.syncSearchFromValue(option.value)
				this.closeAndBlur()
			},
			OnArrowDown(event) {
				event.preventDefault()
				this.focused = true
				this.moveHighlightedIndex(1)
			},
			OnArrowUp(event) {
				event.preventDefault()
				this.focused = true
				this.moveHighlightedIndex(-1)
			},
			OnEnter(event) {
				event.preventDefault()

				if (!this.isOpen) {
					this.isOpen = true
					this.focused = true
					this.syncHighlightedIndex()
					return
				}

				const option = this.filteredOptions[this.highlightedIndex] || this.filteredOptions[0]

				if (option) {
					this.Select(option)
				}
			},
			ClearSearch() {
				this.isUserTyping = false
				this.search = ''
				this.$emit('input', null)
				if (this.$refs.input) {
					this.$refs.input.focus()
				}
			},
		},
		beforeMount() {
			this.uniqueId = useGetUniqueId('autocomplete')
		},
		mounted() {
			this.optionWasSelected = true
		},
		beforeDestroy() {
			window.removeEventListener('click', this.handleClickOutside)
		},
	}
</script>

<style lang="scss" scoped>
	.autocomplete-container {
		width: 100%;
		position: relative;

		.input-container {
			width: 100%;
			display: flex;
			align-items: center;

			.input-wrapper {
				width: 100%;
				position: relative;

				input.form-control {
					text-transform: uppercase;

					&:not(:read-only) {
						padding: 0 1.5rem;
					}
				}

				i {
					position: absolute;
					top: 50%;
					transform: translateY(-50%);
					z-index: 5;

					&.toggle-icon {
						left: 0.5rem;
						padding-top: 0.2rem;
						color: var(--iconColor);
						font-size: 0.8rem;
						pointer-events: none;
						transition: transform 0.1s ease-in-out;
					}

					&.clear-icon {
						right: 0.2rem;
						color: var(--newPrimaryBackgroundColor);
						cursor: pointer;
						padding: 0.25rem;

						&:hover {
							color: var(--newPrimaryFocusColor);
						}
					}
				}

				.readonly-overlay {
					position: absolute;
					top: 0;
					left: 0;
					width: 100%;
					height: 100%;
					cursor: not-allowed;
				}

				&.focused,
				&.filled {
					input.form-control:not(:read-only) {
						background-color: var(--newPrimaryFocusBackgroundColor);
						color: var(--newPrimaryFocusColor);
						outline: none;
						caret-color: var(--newPrimaryFocusColor);
						padding-right: 1.8rem;
					}

					i.toggle-icon {
						color: var(--newPrimaryFocusColor);
					}
				}

				&.disabled {
					input.form-control:disabled {
						padding: 0 1.5rem;
					}

					i.toggle-icon {
						color: var(--disabledColor);
					}
				}
			}

			&.with-buttons {
				display: flex;
				align-items: center;
				gap: 0.25rem;

				input.form-control {
					padding-right: 6rem;
				}

				.buttons-container {
					display: flex;
					gap: 0.5rem;
				}
			}
		}

		// &.is-open {
		// 	.input-group {
		// 		.toggle-icon {
		// 			transform: translateY(-50%) rotate(180deg);
		// 		}
		// 	}
		// }

		.select-button {
			width: 100%;
			padding: 1.25rem 1rem;
			color: var(--newPrimaryActiveColor);
			background-color: var(--newPrimaryActiveBackgroundColor);
			border: 1px solid var(--newPrimaryActiveBackgroundColor);
			border-radius: 0.3rem;
			display: flex;
			justify-content: space-between;
			align-items: center;
			cursor: pointer;
			font-weight: 900;
			font-size: 1.5rem;
			line-height: 2rem;

			.select-button-label {
				width: 100%;
				text-align: center;
			}

			&:hover {
				color: var(--newPrimaryHoverColor);
				background-color: var(--newPrimaryHoverBackgroundColor);
				border-color: var(--newPrimaryHoverBackgroundColor);
			}

			.select-button-icon {
				transition: transform 0.1s ease-in-out;
			}

			&.is-open {
				.select-button-icon {
					transform: rotate(180deg);
				}
			}
		}

		.options-list {
			--listBackgroundColor: #0e223d;
			--listBorderColor: #314977;

			position: absolute;
			top: 100%;
			left: 0;
			width: 100%;
			max-height: calc(46vh);
			overflow-y: auto;
			background-color: var(--listBackgroundColor);
			border: 1px solid var(--listBorderColor);
			border-radius: 0.3rem;
			padding: 0;
			z-index: 10;

			.option-item {
				padding: 0.75rem 0.875rem;
				list-style: none;
				border: 1px solid transparent;
				font-weight: 300;
				font-size: 0.75rem;
				color: var(--fontColorSecondary);
				transition: all 0.1s ease-in-out;
				cursor: pointer;

				&.highlighted {
					color: var(--white);
					border-color: var(--newPrimaryFocusBackgroundColor);
					background-color: rgba(49, 73, 119, 0.35);
				}

				&:hover {
					color: var(--white);
					border-color: var(--newPrimaryFocusBackgroundColor);
				}

				&.no-options {
					font-weight: 900;
					text-align: center;
				}
			}
		}
	}

	::-webkit-scrollbar {
		width: 10px;
	}

	::-webkit-scrollbar-thumb {
		background-color: var(--newPrimaryBackgroundColor);

		&:hover {
			background-color: var(--newPrimaryHoverBackgroundColor);
		}
	}
</style>
